// Copyright (c) 2021 Celadon Development LLC, All rights reserved.
// Author Alexander Mikholap <alexander.mikholap@celadon.ae>
import {
  call,
  put,
  select,
  all,
  takeEvery
} from 'redux-saga/effects';

import { isEmptyOrNull } from '../../../services/stringHelper';
import { CRAFTY_CLICKS_API_KEY } from '../../../config/apiKeys';

import * as types from './actionTypes';
import {
  getTotal
} from '../../basket/store/selectors';
import * as issueVoucherActions from './actions';
import * as voucherConfirmedActions from '../../voucher-confirmed/store/actions';
import {
  getBookshopCustomer,
  getRequestStatus,
} from './selectors';

import {
  validateEmailService,
  validateMobileService,
  issueVoucherService,
} from './service';
import history from '../../../services/history';


function* validationHandler(response, message) {
  if (response.ok) {
    const status = response.body.result;
    if (!status) {
      yield put(issueVoucherActions.setRequestStatus(status, message));
    }
  } else {
    const status = false;
    yield put(issueVoucherActions.setRequestStatus(status, 'Server error'));
  }
}


function* validateCustomerFormSaga() {
  const bookshopCustomer = yield select(getBookshopCustomer);

  // Mobile is optional (client, 2026-09-14): plenty of customers at a till
  // will not give a number, and refusing to issue their voucher over it is
  // worse than holding no number. A number that IS given is still checked, so
  // a typo does not quietly become an unreachable contact.
  const hasMobile = !isEmptyOrNull(bookshopCustomer.mobile);

  if (
    !isEmptyOrNull(bookshopCustomer.fullName)
      && !isEmptyOrNull(bookshopCustomer.email)
  ) {
    const data = {
      key: CRAFTY_CLICKS_API_KEY,
      country: 'gbr',
      phone_number: bookshopCustomer.mobile,
      email: bookshopCustomer.email,
    };

    const { validateEmail, validateMobile } = yield all({
      validateEmail: call(validateEmailService, data),
      // Not called at all when the field is blank - CraftyClicks would answer
      // "invalid" for an empty number and block the voucher.
      validateMobile: hasMobile ? call(validateMobileService, data) : null,
    });

    yield call(validationHandler, validateEmail, 'Invalid email.');

    if (hasMobile) {
      yield call(validationHandler, validateMobile, 'Invalid mobile.');
    }

  } else {
    const status = false;
    const message = 'Enter a name and email for the customer.';

    yield put(issueVoucherActions.setRequestStatus(status, message));
  }
}

function* issueVoucherSaga() {
  yield put(issueVoucherActions.startLoader());

  yield call(validateCustomerFormSaga);

  const requestStatus = yield select(getRequestStatus);

  if (requestStatus.status) {

    const total = yield select(getTotal);
    const CustomerId = localStorage.getItem('loggedID');
    const Token = localStorage.getItem('token');
    const bookshopCustomer = yield select(getBookshopCustomer);

    const data = {
      CustomerId,
      Token,
      FullName: bookshopCustomer.fullName,
      Email: bookshopCustomer.email,
      Mobile: bookshopCustomer.mobile,
      OrderId: total.orderId,
    };

    const response = yield call(issueVoucherService, data);

    if (response.ok) {
      yield put(voucherConfirmedActions.setBookshopCustomer(response.body));
      yield put(issueVoucherActions.resetBookshopCustomerForm());

      history.push('/account/vouchers/confirmed');
    } else {
      const status = false;
      yield put(issueVoucherActions.setRequestStatus(status, response.body.Reason));
    }
  }
  yield put(issueVoucherActions.stopLoader());
}


const issueVoucherSagas = [
  takeEvery(types.VALIDATE_BOOKSHOP_CUSTOMER_FORM, validateCustomerFormSaga),
  takeEvery(types.ISSUE_VOUCHER, issueVoucherSaga),
];

export default issueVoucherSagas;
